import { ITicket, TicketSilo } from "interfaces/entities/ticket";
import { ActionsUnion, IActionWithPayload, createAction } from "store/helper";

import {
  CRUDAction,
  IEntityFetch,
  IEntityFetchSuccess,
  IEntityUpdateSuccess,
  IRequestError,
  IRequestErrorWithDetails,
  IRequestFailure,
  RequestAction,
  createEntityActionNames,
  createEntityActions,
  createRequestTypes,
} from "../actions";
import { IAgent } from "../agents";

import {
  IAddTicketTagsPayload,
  IApplyMacroPayload,
  ICreateTicketFailurePayload,
  ICreateTicketPayload,
  IDeleteTicketPayload,
  IDeleteTicketSuccessPayload,
  IFetchClosestTicketPayload,
  IFetchClosestTicketSuccessPayload,
  IFetchRecentTicketsFailurePayload,
  IFetchRecentTicketsPayload,
  IFetchRecentTicketsSuccessPayload,
  IFetchSimilarTicketsFailurePayload,
  IFetchSimilarTicketsPayload,
  IFetchSimilarTicketsSuccessPayload,
  IFetchTagSuggestionsPayload,
  IFetchTagSuggestionsSuccessPayload,
  IFetchTicketPayload,
  IFetchTicketsCollectionPayload,
  IFetchTicketsToMergeFailurePayload,
  IFetchTicketsToMergePayload,
  IFetchTicketsToMergeSuccessPayload,
  IRemoveTicketTagsPayload,
  ISendTicketMessageFailurePayload,
  ISendTicketMessagePayload,
  ISendTicketRatingPayload,
  ISummarizeTicketFailurePayload,
  ISummarizeTicketPayload,
  IUnspamPayload,
  IUpdateAssignedTeamPayload,
  IUpdatePriorityPayload,
  IUpdateStatusPayload,
  IUpdateTicketAssigneePayload,
  IUpdateTicketCCsPayload,
  IUpdateTicketCustomFieldPayload,
  IUpdateTicketRequesterPayload,
  IUpdateTicketSiloPayload,
  IUpdateTicketSubjectPayload,
  IUpdateTicketTagsPayload,
} from "./interfaces";

export const TicketSelectorsNames = {
  SEND_RATING: "ENTITIES/TICKET/SEND_RATING",
};

export const TICKET = {
  ...createEntityActionNames("TICKET"),
  CREATE: createRequestTypes("ENTITIES/TICKET/CREATE"),
  DELETE: createRequestTypes("ENTITIES/TICKET/DELETE"),
  UPDATE_TAGS: createRequestTypes("ENTITIES/TICKET/UPDATE_TAGS"),
  ADD_TAGS: createRequestTypes("ENTITIES/TICKET/ADD_TAGS"),
  REMOVE_TAG: createRequestTypes("ENTITIES/TICKET/REMOVE_TAG"),
  UPDATE_SUBJECT: createRequestTypes("ENTITIES/TICKET/UPDATE_SUBJECT"),
  UPDATE_REQUESTER: createRequestTypes("ENTITIES/TICKET/UPDATE_REQUESTER"),
  UPDATE_CCS: createRequestTypes("ENTITIES/TICKET/UPDATE_CCS"),
  UPDATE_ASSIGNEE: createRequestTypes("ENTITIES/TICKET/UPDATE_ASSIGNEE"),
  UPDATE_ASSIGNED_TEAM: createRequestTypes("ENTITIES/TICKET/UPDATE_ASSIGNED_TEAM"),
  UPDATE_PRIORITY: createRequestTypes("ENTITIES/TICKET/UPDATE_PRIORITY"),
  UPDATE_STATUS: createRequestTypes("ENTITIES/TICKET/UPDATE_STATUS"),
  SEND_MESSAGE: createRequestTypes("ENTITIES/TICKET/SEND_MESSAGE"),
  UNSPAM: createRequestTypes("ENTITIES/TICKET/UNSPAM"),
  FETCH_RECENT_TICKETS: createRequestTypes("ENTITIES/TICKET/FETCH_RECENT_TICKETS"),
  FETCH_TAG_SUGGESTIONS: createRequestTypes("ENTITIES/TICKET/FETCH_TAG_SUGGESTIONS"),
  FETCH_SIMILAR_TICKETS: createRequestTypes("ENTITIES/TICKET/FETCH_SIMILAR_TICKETS"),
  FETCH_TICKETS_TO_MERGE: createRequestTypes("ENTITIES/TICKET/FETCH_TICKETS_TO_MERGE"),
  SEND_RATING: createRequestTypes(TicketSelectorsNames.SEND_RATING),
  UPDATE_CUSTOM_FIELD: createRequestTypes("ENTITIES/TICKET/UPDATE_CUSTOM_FIELD"),
  UPDATE_SILO: createRequestTypes("ENTITIES/TICKET/UPDATE_SILO"),
  FETCH_CLOSEST_TICKET: createRequestTypes("ENTITIES/TICKET/FETCH_CLOSEST_TICKET"),
  SUMMARIZE_TICKET: createRequestTypes("ENTITIES/TICKET/SUMMARIZE_TICKET"),
  APPLY_MACRO: createRequestTypes("ENTITIES/TICKET/APPLY_MACRO"),
};

const createTicketActions = {
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  createTicket: (payload: ICreateTicketPayload) => createAction(TICKET.CREATE[RequestAction.REQUEST], payload),
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  createTicketSuccess: (payload: ITicket) => createAction(TICKET.CREATE[RequestAction.SUCCESS], payload),
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  createTicketFailure: (payload: ICreateTicketFailurePayload) =>
    createAction(TICKET.CREATE[RequestAction.FAILURE], payload),
};

const deleteTicketActions = {
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  deleteTicket: (payload: IDeleteTicketPayload) => createAction(TICKET.DELETE[RequestAction.REQUEST], payload),
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  deleteTicketSuccess: (payload: IDeleteTicketSuccessPayload) =>
    createAction(TICKET.DELETE[RequestAction.SUCCESS], payload),
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  deleteTicketFailure: (payload: IRequestFailure) => createAction(TICKET.DELETE[RequestAction.FAILURE], payload),
};

const updateTagsActions = {
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  updateTags: (payload: IUpdateTicketTagsPayload) => createAction(TICKET.UPDATE_TAGS[RequestAction.REQUEST], payload),
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  updateTagsSuccess: (payload: ITicket, agent: IAgent) =>
    createAction(TICKET.UPDATE_TAGS[RequestAction.SUCCESS], { ticket: payload, agent }),
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  updateTagsFailure: (payload: IRequestFailure) => createAction(TICKET.UPDATE_TAGS[RequestAction.FAILURE], payload),
};

const addTagsActions = {
  addTags: (payload: IAddTicketTagsPayload): IActionWithPayload<string, IAddTicketTagsPayload> =>
    createAction(TICKET.ADD_TAGS[RequestAction.REQUEST], payload),
  addTagsSuccess: (payload: ITicket, agent: IAgent): IActionWithPayload<string, { ticket: ITicket; agent: IAgent }> =>
    createAction(TICKET.ADD_TAGS[RequestAction.SUCCESS], { ticket: payload, agent }),
  addTagsFailure: (payload: IRequestFailure): IActionWithPayload<string, IRequestFailure> =>
    createAction(TICKET.ADD_TAGS[RequestAction.FAILURE], payload),
};

const removeTagActions = {
  removeTag: (payload: IRemoveTicketTagsPayload): IActionWithPayload<string, IRemoveTicketTagsPayload> =>
    createAction(TICKET.REMOVE_TAG[RequestAction.REQUEST], payload),
  removeTagSuccess: (payload: ITicket): IActionWithPayload<string, ITicket> =>
    createAction(TICKET.REMOVE_TAG[RequestAction.SUCCESS], payload),
  removeTagFailure: (payload: IRequestFailure): IActionWithPayload<string, IRequestFailure> =>
    createAction(TICKET.REMOVE_TAG[RequestAction.FAILURE], payload),
};

const updateSubjectActions = {
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  updateSubject: (payload: IUpdateTicketSubjectPayload) =>
    createAction(TICKET.UPDATE_SUBJECT[RequestAction.REQUEST], payload),
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  updateSubjectSuccess: (payload: ITicket, agent: IAgent) =>
    createAction(TICKET.UPDATE_SUBJECT[RequestAction.SUCCESS], { ticket: payload, agent }),
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  updateSubjectFailure: (payload: IRequestFailure) =>
    createAction(TICKET.UPDATE_SUBJECT[RequestAction.FAILURE], payload),
};

const updateRequesterActions = {
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  updateRequester: (payload: IUpdateTicketRequesterPayload) =>
    createAction(TICKET.UPDATE_REQUESTER[RequestAction.REQUEST], payload),
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  updateRequesterSuccess: (payload: ITicket, agent: IAgent) =>
    createAction(TICKET.UPDATE_REQUESTER[RequestAction.SUCCESS], { ticket: payload, agent }),
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  updateRequesterFailure: (payload: { error: IRequestErrorWithDetails }) =>
    createAction(TICKET.UPDATE_REQUESTER[RequestAction.FAILURE], payload),
};

const updateCCsActions = {
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  updateCCs: (payload: IUpdateTicketCCsPayload) => createAction(TICKET.UPDATE_CCS[RequestAction.REQUEST], payload),
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  updateCCsSuccess: (payload: ITicket, agent: IAgent) =>
    createAction(TICKET.UPDATE_CCS[RequestAction.SUCCESS], { ticket: payload, agent }),
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  updateCCsFailure: (payload: IRequestFailure) => createAction(TICKET.UPDATE_CCS[RequestAction.FAILURE], payload),
};

const updateAssigneeActions = {
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  updateAssignee: (payload: IUpdateTicketAssigneePayload) =>
    createAction(TICKET.UPDATE_ASSIGNEE[RequestAction.REQUEST], payload),
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  updateAssigneeSuccess: (payload: ITicket, agent: IAgent) =>
    createAction(TICKET.UPDATE_ASSIGNEE[RequestAction.SUCCESS], { ticket: payload, agent }),
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  updateAssigneeFailure: (payload: IRequestFailure) =>
    createAction(TICKET.UPDATE_ASSIGNEE[RequestAction.FAILURE], payload),
};

const updateAssignedTeamActions = {
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  updateAssignedTeam: (payload: IUpdateAssignedTeamPayload) =>
    createAction(TICKET.UPDATE_ASSIGNED_TEAM[RequestAction.REQUEST], payload),
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  updateAssignedTeamSuccess: (payload: ITicket, agent: IAgent) =>
    createAction(TICKET.UPDATE_ASSIGNED_TEAM[RequestAction.SUCCESS], { ticket: payload, agent }),
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  updateAssignedTeamFailure: (payload: IRequestFailure) =>
    createAction(TICKET.UPDATE_ASSIGNED_TEAM[RequestAction.FAILURE], payload),
};

const updatePriorityActions = {
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  updatePriority: (payload: IUpdatePriorityPayload) =>
    createAction(TICKET.UPDATE_PRIORITY[RequestAction.REQUEST], payload),
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  updatePrioritySuccess: (payload: ITicket, agent: IAgent) =>
    createAction(TICKET.UPDATE_PRIORITY[RequestAction.SUCCESS], { ticket: payload, agent }),
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  updatePriorityFailure: (payload: IRequestFailure) =>
    createAction(TICKET.UPDATE_PRIORITY[RequestAction.FAILURE], payload),
};

const updateStatusActions = {
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  updateStatus: (payload: IUpdateStatusPayload) => createAction(TICKET.UPDATE_STATUS[RequestAction.REQUEST], payload),
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  updateStatusSuccess: (payload: ITicket, agent: IAgent) =>
    createAction(TICKET.UPDATE_STATUS[RequestAction.SUCCESS], { ticket: payload, agent }),
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  updateStatusFailure: (payload: IRequestFailure) => createAction(TICKET.UPDATE_STATUS[RequestAction.FAILURE], payload),
};

const sendMessageActions = {
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  sendMessage: (payload: ISendTicketMessagePayload) =>
    createAction(TICKET.SEND_MESSAGE[RequestAction.REQUEST], payload),
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  sendMessageSuccess: (payload: ITicket, originalRequest: ISendTicketMessagePayload, agent: IAgent) =>
    createAction(TICKET.SEND_MESSAGE[RequestAction.SUCCESS], { ticket: payload, originalRequest, agent }),
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  sendMessageFailure: (payload: ISendTicketMessageFailurePayload) =>
    createAction(TICKET.SEND_MESSAGE[RequestAction.FAILURE], payload),
};

const unspamActions = {
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  unspam: (payload: IUnspamPayload) => createAction(TICKET.UNSPAM[RequestAction.REQUEST], payload),
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  unspamSuccess: (ticket: ITicket, agent: IAgent) =>
    createAction(TICKET.UNSPAM[RequestAction.SUCCESS], { ticket, agent }),
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  unspamFailure: () => createAction(TICKET.UNSPAM[RequestAction.FAILURE]),
};

const fetchRecentTicketsActions = {
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  fetchRecentTickets: (payload: IFetchRecentTicketsPayload) =>
    createAction(TICKET.FETCH_RECENT_TICKETS[RequestAction.REQUEST], payload),
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  fetchRecentTicketsSuccess: (payload: IFetchRecentTicketsSuccessPayload) =>
    createAction(TICKET.FETCH_RECENT_TICKETS[RequestAction.SUCCESS], payload),
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  fetchRecentTicketsFailure: (payload: IFetchRecentTicketsFailurePayload) =>
    createAction(TICKET.FETCH_RECENT_TICKETS[RequestAction.FAILURE], payload),
};

const fetchTagSuggestionsActions = {
  fetchTagSuggestions: (
    payload: IFetchTagSuggestionsPayload
  ): IActionWithPayload<string, IFetchTagSuggestionsPayload> =>
    createAction(TICKET.FETCH_TAG_SUGGESTIONS[RequestAction.REQUEST], payload),
  fetchTagSuggestionsSuccess: (
    payload: IFetchTagSuggestionsSuccessPayload
  ): IActionWithPayload<string, IFetchTagSuggestionsSuccessPayload> =>
    createAction(TICKET.FETCH_TAG_SUGGESTIONS[RequestAction.SUCCESS], payload),
  fetchTagSuggestionsFailure: (payload: IRequestFailure): IActionWithPayload<string, IRequestFailure> =>
    createAction(TICKET.FETCH_TAG_SUGGESTIONS[RequestAction.FAILURE], payload),
};

const fetchSimilarTicketsActions = {
  fetchSimilarTickets: (
    payload: IFetchSimilarTicketsPayload
  ): IActionWithPayload<string, IFetchSimilarTicketsPayload> =>
    createAction(TICKET.FETCH_SIMILAR_TICKETS[RequestAction.REQUEST], payload),
  fetchSimilarTicketsSuccess: (
    payload: IFetchSimilarTicketsSuccessPayload
  ): IActionWithPayload<string, IFetchSimilarTicketsSuccessPayload> =>
    createAction(TICKET.FETCH_SIMILAR_TICKETS[RequestAction.SUCCESS], payload),
  fetchSimilarTicketsFailure: (
    payload: IFetchSimilarTicketsFailurePayload
  ): IActionWithPayload<string, IFetchSimilarTicketsFailurePayload> =>
    createAction(TICKET.FETCH_SIMILAR_TICKETS[RequestAction.FAILURE], payload),
};

const fetchTicketsToMergeActions = {
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  fetchtTicketsToMerge: (payload: IFetchTicketsToMergePayload) =>
    createAction(TICKET.FETCH_TICKETS_TO_MERGE[RequestAction.REQUEST], payload),
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  fetchtTicketsToMergeSuccess: (payload: IFetchTicketsToMergeSuccessPayload) =>
    createAction(TICKET.FETCH_TICKETS_TO_MERGE[RequestAction.SUCCESS], payload),
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  fetchtTicketsToMergeFailure: (payload: IFetchTicketsToMergeFailurePayload) =>
    createAction(TICKET.FETCH_TICKETS_TO_MERGE[RequestAction.FAILURE], payload),
};

const sendRatingActions = {
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  sendRating: (payload: ISendTicketRatingPayload) => createAction(TICKET.SEND_RATING[RequestAction.REQUEST], payload),
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  sendRatingSuccess: () => createAction(TICKET.SEND_RATING[RequestAction.SUCCESS]),
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  sendRatingFailure: () => createAction(TICKET.SEND_RATING[RequestAction.FAILURE]),
};

const updateCustomFieldActions = {
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  updateCustomField: (payload: IUpdateTicketCustomFieldPayload) =>
    createAction(TICKET.UPDATE_CUSTOM_FIELD[RequestAction.REQUEST], payload),
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  updateCustomFieldSuccess: (payload: IEntityUpdateSuccess, agent: IAgent) =>
    createAction(TICKET.UPDATE_CUSTOM_FIELD[RequestAction.SUCCESS], { ticket: payload, agent }),
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  updateCustomFieldFailure: (payload: IRequestFailure) =>
    createAction(TICKET.UPDATE_CUSTOM_FIELD[RequestAction.FAILURE], payload),
};

const updateSiloActions = {
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  updateSilo: (payload: IUpdateTicketSiloPayload) => createAction(TICKET.UPDATE_SILO[RequestAction.REQUEST], payload),
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  updateSiloSuccess: (payload: TicketSilo) => createAction(TICKET.UPDATE_SILO[RequestAction.SUCCESS], payload),
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  updateSiloFailure: (payload: { silo: TicketSilo; error: IRequestError }) =>
    createAction(TICKET.UPDATE_SILO[RequestAction.FAILURE], payload),
};

const fetchClosestTicketActions = {
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  fetchClosestTicket: (payload: IFetchClosestTicketPayload) =>
    createAction(TICKET.FETCH_CLOSEST_TICKET[RequestAction.REQUEST], payload),
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  fetchClosestTicketSuccess: (payload: IFetchClosestTicketSuccessPayload) =>
    createAction(TICKET.FETCH_CLOSEST_TICKET[RequestAction.SUCCESS], payload),
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  fetchClosestTicketFailure: (payload: IRequestFailure) =>
    createAction(TICKET.FETCH_CLOSEST_TICKET[RequestAction.FAILURE], payload),
};

const summarizeTicketActions = {
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  summarizeTicket: (payload: ISummarizeTicketPayload) =>
    createAction(TICKET.SUMMARIZE_TICKET[RequestAction.REQUEST], payload),
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  summarizeTicketSuccess: (payload: ISummarizeTicketPayload) =>
    createAction(TICKET.SUMMARIZE_TICKET[RequestAction.SUCCESS], payload),
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  summarizeTicketFailure: (payload: ISummarizeTicketFailurePayload) =>
    createAction(TICKET.SUMMARIZE_TICKET[RequestAction.FAILURE], payload),
};

const applyMacroActions = {
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  applyMacro: (payload: IApplyMacroPayload) => createAction(TICKET.APPLY_MACRO[RequestAction.REQUEST], payload),
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
  applyMacroSuccess: (payload: any) => createAction(TICKET.APPLY_MACRO[RequestAction.SUCCESS], payload),
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  applyMacroFailure: (payload: IRequestFailure) => createAction(TICKET.APPLY_MACRO[RequestAction.FAILURE], payload),
};

export const TicketActions = {
  ...createEntityActions<ITicket, IFetchTicketPayload, IFetchTicketsCollectionPayload>(TICKET),
  ...createTicketActions,
  ...deleteTicketActions,
  ...addTagsActions,
  ...removeTagActions,
  ...updateTagsActions,
  ...updateSubjectActions,
  ...updateRequesterActions,
  ...updateCCsActions,
  ...updateAssigneeActions,
  ...updateAssignedTeamActions,
  ...sendMessageActions,
  ...unspamActions,
  ...fetchRecentTicketsActions,
  ...fetchTagSuggestionsActions,
  ...fetchSimilarTicketsActions,
  ...fetchTicketsToMergeActions,
  ...sendRatingActions,
  ...updatePriorityActions,
  ...updateStatusActions,
  ...updateCustomFieldActions,
  ...updateSiloActions,
  ...fetchClosestTicketActions,
  ...summarizeTicketActions,
  ...applyMacroActions,
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  fetch: (payload: IEntityFetch & { overwriteCurrent?: boolean; incrementCounter?: boolean }) =>
    createAction(TICKET[CRUDAction.FETCH_SINGLE][RequestAction.REQUEST], payload),
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  fetchSuccess: (
    payload: IEntityFetchSuccess<ITicket> & {
      agent: IAgent;
      overwriteCurrent?: boolean;
      incrementCounter?: boolean;
    }
  ) => createAction(TICKET[CRUDAction.FETCH_SINGLE][RequestAction.SUCCESS], payload),
};

export type TicketAction = ActionsUnion<typeof TicketActions>;
